import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import { Edit as EditIcon, Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Employee } from "../../../types/leave";
import { TimeEntry, formatDuration } from "../../../types/timetracking";
import {
  formatDate,
  formatTime,
  formatDateOnly,
} from "../../../services/dateTimeService";
import leaveApiService from "../../../services/leave-api";
import { timeTrackingApi } from "../../../services/timetracking-api";
import EditTimesheetDialog from "./EditTimesheetDialog";

const EmployeeTimesheet: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  // Initialize with current month (first day of current month)
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null);
  const [dialogMode, setDialogMode] = useState<"edit" | "add">("edit");

  // Load all employees on mount
  useEffect(() => {
    loadAllEmployees();
  }, []);

  // Load timesheet when employee or month changes
  useEffect(() => {
    if (selectedEmployee) {
      loadEmployeeTimesheet();
    } else {
      setTimeEntries([]);
    }
  }, [selectedEmployee, selectedMonth]);

  const loadAllEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const employeeList = await leaveApiService.getAllEmployees(0, 1000);
      setAllEmployees(employeeList);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToLoadEmployees"),
      );
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeTimesheet = async () => {
    if (!selectedEmployee) return;

    try {
      setLoading(true);
      setError(null);

      // Calculate start and end dates for the selected month
      const startDate = new Date(
        selectedMonth.getFullYear(),
        selectedMonth.getMonth(),
        1,
      );
      const endDate = new Date(
        selectedMonth.getFullYear(),
        selectedMonth.getMonth() + 1,
        0,
      );

      const startDateStr = formatDateOnly(startDate);
      const endDateStr = formatDateOnly(endDate);

      const entries = await timeTrackingApi.getTimeEntriesForDateRange(
        selectedEmployee.id,
        startDateStr,
        endDateStr,
      );

      setTimeEntries(entries);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToLoadTimesheet"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Sort employees by office location and group them
  const sortedEmployees = useMemo(() => {
    return [...allEmployees].sort((a, b) => {
      const locationA = a.officeLocation || t("common.noOfficeLocation");
      const locationB = b.officeLocation || t("common.noOfficeLocation");
      if (locationA !== locationB) {
        return locationA.localeCompare(locationB);
      }
      return `${a.firstName} ${a.lastName}`.localeCompare(
        `${b.firstName} ${b.lastName}`,
      );
    });
  }, [allEmployees]);

  // Generate month options (current month and previous 11 months)
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push(date);
    }
    return options;
  }, []);

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
  };

  const handleEditEntry = (entry: TimeEntry) => {
    setDialogMode("edit");
    setSelectedEntry(entry);
    setEditDialogOpen(true);
  };

  const handleAddEntry = () => {
    setDialogMode("add");
    setSelectedEntry(null);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setSelectedEntry(null);
    loadEmployeeTimesheet();
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setSelectedEntry(null);
  };

  /**
   * For each date, sum totalHours across all entries and decide whether the
   * workday is completed (8h 45m net work = 8.75h, or 4h with half-day leave
   * which the backend already flags via isWorkdayCompleted on any entry).
   * Returns a map of dateString → boolean.
   */
  const dailyCompletionMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    const hoursByDate: Record<string, number> = {};
    const halfDayByDate: Record<string, boolean> = {};

    for (const entry of timeEntries) {
      const date = formatDate(entry.timeIn);
      hoursByDate[date] = (hoursByDate[date] ?? 0) + (entry.totalHours ?? 0);
      // If any entry in a day has half-day leave flagged, carry it for the day
      if (entry.hasHalfDayLeave) {
        halfDayByDate[date] = true;
      }
    }

    for (const date of Object.keys(hoursByDate)) {
      const total = hoursByDate[date];
      const isHalfDay = halfDayByDate[date] ?? false;
      map[date] =
        total >= 8.75 || (isHalfDay && total >= 4);
    }

    return map;
  }, [timeEntries]);

  const getStatusColor = (
    status: string,
    isWorkdayCompleted?: boolean,
  ): "success" | "warning" | "error" | "default" => {
    if (status === "OUT") {
      return isWorkdayCompleted ? "success" : "warning";
    }
    return "default";
  };

  const getStatusText = (status: string, isWorkdayCompleted?: boolean) => {
    if (status === "IN") {
      return "timetracking.status.in";
    }
    if (status === "OUT") {
      return isWorkdayCompleted
        ? "timetracking.status.completed"
        : "timetracking.status.incomplete";
    }
    return "timetracking.status.unknown";
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t("timetracking.employeeTimesheet.title")}
        </Typography>

        {/* Employee Selection, Month Selector, and Add Button */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mt: 3,
            mb: 3,
            alignItems: "flex-start",
          }}
        >
          {/* Employee Dropdown */}
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Autocomplete
              options={sortedEmployees}
              value={selectedEmployee}
              onChange={(_, newValue) => setSelectedEmployee(newValue)}
              groupBy={(option) =>
                option.officeLocation || t("common.noOfficeLocation")
              }
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName} (${option.department || "N/A"})`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("timetracking.employeeTimesheet.selectEmployee")}
                  placeholder={t(
                    "timetracking.employeeTimesheet.searchEmployee",
                  )}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    {option.firstName} {option.lastName}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      ({option.department || "N/A"})
                    </Typography>
                  </li>
                );
              }}
            />
          </Box>

          {/* Month Selector */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t("timetracking.employeeTimesheet.month")}</InputLabel>
            <Select
              value={selectedMonth.getTime()}
              onChange={(e) =>
                setSelectedMonth(new Date(e.target.value as number))
              }
              label={t("timetracking.employeeTimesheet.month")}
            >
              {monthOptions.map((month) => (
                <MenuItem key={month.getTime()} value={month.getTime()}>
                  {formatMonthYear(month)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Add Entry Button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddEntry}
            disabled={!selectedEmployee}
            sx={{ minWidth: 150, height: 56 }}
          >
            {t("timetracking.employeeTimesheet.addEntry")}
          </Button>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* No Employee Selected */}
        {!loading && !selectedEmployee && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">
              {t("timetracking.employeeTimesheet.selectEmployeePrompt")}
            </Typography>
          </Box>
        )}

        {/* Timesheet Table */}
        {!loading && selectedEmployee && timeEntries.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">
              {t("timetracking.employeeTimesheet.noEntries")}
            </Typography>
          </Box>
        )}

        {!loading && selectedEmployee && timeEntries.length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("timetracking.table.date")}</TableCell>
                  <TableCell>{t("timetracking.table.timeIn")}</TableCell>
                  <TableCell>{t("timetracking.table.timeOut")}</TableCell>
                  <TableCell>{t("timetracking.labels.breakTime")}</TableCell>
                  <TableCell>{t("timetracking.table.totalHours")}</TableCell>
                  <TableCell>{t("timetracking.table.status")}</TableCell>
                  <TableCell>{t("timetracking.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeEntries.map((entry, index) => {
                  // Calculate if this is the first entry for this date
                  const currentDate = formatDate(entry.timeIn);
                  const isFirstOfDate =
                    index === 0 ||
                    formatDate(timeEntries[index - 1].timeIn) !== currentDate;

                  // Calculate rowspan for this date (count consecutive entries with same date)
                  let rowspan = 1;
                  if (isFirstOfDate) {
                    for (let i = index + 1; i < timeEntries.length; i++) {
                      if (formatDate(timeEntries[i].timeIn) === currentDate) {
                        rowspan++;
                      } else {
                        break;
                      }
                    }
                  }

                  return (
                    <TableRow key={entry.id} hover>
                      {/* Only render date cell for first entry of each date */}
                      {isFirstOfDate && (
                        <TableCell
                          rowSpan={rowspan}
                          sx={{
                            verticalAlign: "middle",
                            borderRight: "1px solid",
                            borderRightColor: "divider",
                            bgcolor: "action.hover",
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {currentDate}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(entry.timeIn)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {entry.timeOut ? formatTime(entry.timeOut) : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {entry.totalBreakMinutes &&
                          entry.totalBreakMinutes > 0
                            ? `${Math.floor(entry.totalBreakMinutes / 60)}h ${entry.totalBreakMinutes % 60}m`
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDuration(entry.totalHours)}
                        </Typography>
                      </TableCell>
                      {/* Status — shown once per date, spanning all rows for that date.
                          Uses the combined daily hours so multiple sessions are treated as one workday. */}
                      {isFirstOfDate && (() => {
                        const dayCompleted = dailyCompletionMap[currentDate] ?? false;
                        // Determine the representative status for the day:
                        // If any entry is still IN (active session), show IN; otherwise show OUT.
                        const entriesForDate = timeEntries.filter(
                          (e) => formatDate(e.timeIn) === currentDate,
                        );
                        const dayStatus = entriesForDate.some((e) => e.status === "IN")
                          ? "IN"
                          : "OUT";
                        return (
                          <TableCell rowSpan={rowspan} sx={{ verticalAlign: "middle" }}>
                            <Chip
                              label={t(getStatusText(dayStatus, dayCompleted))}
                              color={getStatusColor(dayStatus, dayCompleted) as any}
                              size="small"
                            />
                          </TableCell>
                        );
                      })()}
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditEntry(entry)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit/Add Dialog */}
      <EditTimesheetDialog
        open={editDialogOpen}
        entry={selectedEntry}
        employeeId={selectedEmployee?.id}
        employeeName={
          selectedEmployee
            ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
            : undefined
        }
        mode={dialogMode}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />
    </Box>
  );
};

export default EmployeeTimesheet;
